import { useState } from 'react';
import { Plus, Trash2, ChevronDown, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/frontend/components/ui/button';
import { Input } from '@/frontend/components/ui/input';
import { Label } from '@/frontend/components/ui/label';
import { Textarea } from '@/frontend/components/ui/textarea';
import { MultiImageUploadField } from '@/frontend/components/admin/MultiImageUploadField';
import { cn } from '@/frontend/lib/utils';
import { CommonArea } from '@/shared/types';

export interface CommonAreasFieldsProps {
  values: CommonArea[];
  onChange: (areas: CommonArea[]) => void;
  storagePathPrefix: string;
  onUploadingChange?: (uploading: boolean) => void;
}

export function CommonAreasFields({
  values,
  onChange,
  storagePathPrefix,
  onUploadingChange,
}: CommonAreasFieldsProps) {
  // Single-open accordion: index of the expanded area, or null when all collapsed.
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const updateArea = (index: number, patch: Partial<CommonArea>) => {
    onChange(values.map((area, i) => (i === index ? { ...area, ...patch } : area)));
  };

  const addArea = () => {
    onChange([...values, { name: '', description: '', imagesUrl: [] }]);
    setOpenIndex(values.length); // auto-expand the newly added (empty) area
  };

  const removeArea = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
    // Keep the open card pointing at the same area after the list shifts.
    setOpenIndex((prev) => {
      if (prev === null) return null;
      if (prev === index) return null;
      return prev > index ? prev - 1 : prev;
    });
  };

  return (
    <div className="space-y-3">
      {values.length > 0 && (
        <div className="space-y-2">
          {values.map((area, index) => {
            const isOpen = openIndex === index;
            const count = area.imagesUrl.length;
            return (
              <div key={index} className="border rounded-lg overflow-hidden">
                {/* Collapsed header: image icon + name + photo count */}
                <div
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50"
                  onClick={() => toggle(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(index);
                    }
                  }}
                >
                  <ImageIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {area.name.trim() || 'Untitled area'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {count} {count === 1 ? 'photo' : 'photos'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeArea(index);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform',
                      isOpen && 'rotate-180'
                    )}
                  />
                </div>

                {/* Expanded body: editable fields */}
                {isOpen && (
                  <div className="space-y-3 p-4 pt-0 border-t">
                    <div className="space-y-2 pt-3">
                      <Label htmlFor={`common-area-name-${index}`}>Area name</Label>
                      <Input
                        id={`common-area-name-${index}`}
                        value={area.name}
                        onChange={(e) => updateArea(index, { name: e.target.value })}
                        placeholder="e.g. Kitchen, Living Room, Bathroom"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`common-area-desc-${index}`}>
                        Description{' '}
                        <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <Textarea
                        id={`common-area-desc-${index}`}
                        value={area.description || ''}
                        onChange={(e) => updateArea(index, { description: e.target.value })}
                        rows={2}
                        placeholder="Optional caption for this area..."
                      />
                    </div>

                    <MultiImageUploadField
                      values={area.imagesUrl}
                      onChange={(urls) => updateArea(index, { imagesUrl: urls })}
                      // Flat path: the Storage rule `residences/{id}/{fileName}` only
                      // permits a single segment under the residence id, so we upload
                      // alongside the hero image (filenames are timestamp-unique).
                      storagePathPrefix={storagePathPrefix}
                      label="Photos"
                      onUploadingChange={onUploadingChange}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Button type="button" variant="outline" onClick={addArea}>
        <Plus className="h-4 w-4 mr-2" />
        Add common area
      </Button>
    </div>
  );
}
